import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../firebaseConfig";
import styles from "../Styles/MobileNavigation.module.css";

import { useUsers } from "../Commoncontext/UsersContext";
import { useSelectedCompany } from "../Commoncontext/SelectedCompanyContext";
import { useCompanyConfig } from "../Commoncontext/CompanyConfigContext";
import {
  getVisibleNavigationItems,
  getMobileNavigationItems,
  getCurrentNavigationLabel,
} from "../Utils/navigationUtils";

function MobileNavigation({ onMenuClick }) {
  const { user } = useUsers();
  const { selectedCompanyId } = useSelectedCompany();
  const { enabledItems } = useCompanyConfig();
  const location = useLocation();
  const navigate = useNavigate();

  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef(null);

  const visibleItems = useMemo(() => {
    return getVisibleNavigationItems({
      enabledItems,
      user,
      selectedCompanyId,
    });
  }, [enabledItems, user, selectedCompanyId]);

  const mobileItems = useMemo(() => {
    return getMobileNavigationItems(visibleItems);
  }, [visibleItems]);

  const currentPageTitle = useMemo(() => {
    const pathname = location.pathname || "";
    if (pathname.startsWith("/owninformation")) {
      return "Omat tiedot";
    }
    if (pathname.startsWith("/announcementnew2")) {
      return "Tiedotteet";
    }
    return getCurrentNavigationLabel({
      visibleItems,
      mobileItems,
      pathname,
      fallback: "AsukasHuudi",
    });
  }, [visibleItems, mobileItems, location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target)
      ) {
        setIsProfileMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsProfileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  useEffect(() => {
    setIsProfileMenuOpen(false);
  }, [location.pathname]);

  const handleProfileClick = () => {
    setIsProfileMenuOpen((prev) => !prev);
  };

  const handleOpenOwnInformation = () => {
    setIsProfileMenuOpen(false);
    navigate("/owninformation");
  };

  const handleLogout = async () => {
    setIsProfileMenuOpen(false);
    await signOut(auth);
    localStorage.removeItem("users_cache_etag_v1");
    localStorage.removeItem("sr_cache_etag_v1");
    localStorage.removeItem("ann_cache_etag_v2");
    localStorage.removeItem("documents_cache_etag_v1");
    window.location.href = "/login";
  };

  const renderProfile = () => {
    if (user?.profiilikuvaurl) {
      return (
        <img
          src={user.profiilikuvaurl}
          alt="Profiili"
          className={styles.profileImage}
        />
      );
    }

    const initials = `${user?.etunimi?.[0] || ""}${user?.sukunimi?.[0] || ""}`.trim();

    return <div className={styles.profileFallback}>{initials || "👤"}</div>;
  };

  return (
    <div className={styles.mobileNav}>
      <div className={styles.topBar}>
        <div className={styles.brand}>
          <img
            src="/logooranssi.png"
            alt="AsukasHuudi logo"
            className={styles.brandLogo}
          />
          <span className={styles.brandText}>AsukasHuudi</span>
        </div>

        <div className={styles.profileMenuWrap} ref={profileMenuRef}>
          <button
            type="button"
            className={styles.profileButton}
            onClick={handleProfileClick}
            aria-label="Avaa käyttäjävalikko"
            title="Käyttäjävalikko"
          >
            {renderProfile()}
          </button>

          {isProfileMenuOpen && (
            <div className={styles.profileDropdown}>
              <button
                type="button"
                className={styles.profileDropdownItem}
                onClick={handleOpenOwnInformation}
              >
                Omat tiedot
              </button>
              <button
                type="button"
                className={`${styles.profileDropdownItem} ${styles.logoutItem}`}
                onClick={handleLogout}
              >
                Kirjaudu ulos
              </button>
            </div>
          )}
        </div>
      </div>

      <div className={styles.sectionBar}>
        <button
          className={styles.menuButton}
          onClick={onMenuClick}
          type="button"
          aria-label="Avaa navigaatio"
          title="Avaa navigaatio"
        >
          ☰
        </button>

        <div className={styles.sectionTitle} title={currentPageTitle}>
          {currentPageTitle}
        </div>
      </div>
    </div>
  );
}

export default MobileNavigation;